import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import api from "../api/api";

import {
    Layout,
    Card,
    Typography,
    Button,
    Input,
    Modal,
    Form,
    Select,
    Row,
    Col,
    Tag,
    Space,
    Spin,
    Empty,
    Avatar,
    Popconfirm,
    message,
    Divider,
    Badge,
} from "antd";

import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    FileProtectOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
    DownloadOutlined,
    FilterOutlined,
} from "@ant-design/icons";

import { useAcademicYear } from "../AcademicYearContext";

const { Title, Text } = Typography;
const { TextArea } = Input;


/* =========================================================
   ALLOWED ROLES
========================================================= */

const ALLOWED_ROLES = [
    "admin",
    "teacher",
    "registry",
    "business manager",
    "vpi",
    "vpa",
    "dean",
];


/* =========================================================
   ROLE HELPERS
========================================================= */

const normalizeRole = (role) =>
    (role || "")
        .trim()
        .toLowerCase();


const getStoredUser = () => {
    const raw = localStorage.getItem("user");

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};


/* =========================================================
   COMPONENT
========================================================= */

const Complaint = () => {
    const {
        selectedYear,
        initialized,
    } = useAcademicYear();


    /* =====================================================
       USER
    ===================================================== */

    const [userType, setUserType] = useState(null);


    /* =====================================================
       STUDENTS
    ===================================================== */

    const [students, setStudents] = useState([]);

    const [loadingStudents, setLoadingStudents] =
        useState(false);


    /* =====================================================
       COMPLAINTS
    ===================================================== */

    const [complaints, setComplaints] = useState([]);

    const [loading, setLoading] = useState(false);


    /* =====================================================
       PERIODS / GRADE CLASSES
    ===================================================== */

    const [periods, setPeriods] = useState([]);

    const [selectedPeriod, setSelectedPeriod] =
        useState(null);


    const [gradeClasses, setGradeClasses] =
        useState([]);

    const [selectedGradeClass, setSelectedGradeClass] =
        useState(null);


    const [chartLoading, setChartLoading] =
        useState(false);


    /* =====================================================
       SEARCH
    ===================================================== */

    const [search, setSearch] = useState("");


    /* =====================================================
       MODAL
    ===================================================== */

    const [modalOpen, setModalOpen] =
        useState(false);


    const [editId, setEditId] =
        useState(null);


    const [selectedStudent, setSelectedStudent] =
        useState(null);


    const [form] = Form.useForm();


    /* =====================================================
       LOAD USER
    ===================================================== */

    useEffect(() => {
        const user = getStoredUser();

        if (user) {
            setUserType(
                normalizeRole(
                    user.user_type ||
                    user.role
                )
            );
        }
    }, []);


    /* =====================================================
       PERMISSION
    ===================================================== */

    const canPost = useMemo(() => {
        return ALLOWED_ROLES.includes(
            normalizeRole(userType)
        );
    }, [userType]);


    /* =====================================================
       FETCH PERIODS
    ===================================================== */

    const fetchPeriods = async () => {
        try {
            const res = await api.get(
                "/api/users/periods/"
            );

            const data =
                Array.isArray(res.data)
                    ? res.data
                    : res.data?.results || [];

            setPeriods(data);
        } catch (error) {
            console.error(
                "FETCH PERIODS ERROR:",
                error
            );

            message.error(
                "Unable to load periods"
            );
        }
    };


    /* =====================================================
       FETCH GRADE CLASSES
    ===================================================== */

    const fetchGradeClasses = async () => {
        try {
            const res = await api.get(
                "/api/grade-classes/"
            );

            const data =
                Array.isArray(res.data)
                    ? res.data
                    : res.data?.results || [];

            setGradeClasses(data);
        } catch (error) {
            console.error(
                "FETCH GRADE CLASSES ERROR:",
                error
            );

            message.error(
                "Unable to load grade classes"
            );
        }
    };


    /* =====================================================
       FETCH ALL STUDENTS
       
       IMPORTANT:
       The students endpoint is paginated.
       The backend normally returns 50 students per page.

       This function continues requesting every page until
       all students for the selected academic year are loaded.
    ===================================================== */

    const fetchStudents = async () => {
        if (!selectedYear?.id) {
            setStudents([]);
            return;
        }

        try {
            setLoadingStudents(true);

            const allStudents = [];

            const pageSize = 50;

            let page = 1;

            let totalCount = null;


            while (true) {
                const response = await api.get(
                    "/api/users/students/",
                    {
                        params: {
                            academic_year:
                                selectedYear.id,
                            page,
                            page_size:
                                pageSize,
                        },
                    }
                );


                const data =
                    response?.data || {};


                let pageStudents = [];


                /* -----------------------------------------
                   ARRAY RESPONSE
                ----------------------------------------- */

                if (Array.isArray(data)) {
                    pageStudents = data;
                }


                /* -----------------------------------------
                   DRF PAGINATED RESPONSE
                ----------------------------------------- */

                else if (
                    Array.isArray(
                        data.results
                    )
                ) {
                    pageStudents =
                        data.results;
                }


                /* -----------------------------------------
                   OTHER POSSIBLE RESPONSE SHAPES
                ----------------------------------------- */

                else if (
                    Array.isArray(
                        data.data
                    )
                ) {
                    pageStudents =
                        data.data;
                }

                else if (
                    Array.isArray(
                        data.students
                    )
                ) {
                    pageStudents =
                        data.students;
                }


                /* -----------------------------------------
                   TOTAL RECORD COUNT
                ----------------------------------------- */

                if (
                    totalCount === null &&
                    typeof data.count ===
                        "number"
                ) {
                    totalCount =
                        data.count;
                }


                /* -----------------------------------------
                   ADD PAGE STUDENTS
                ----------------------------------------- */

                allStudents.push(
                    ...pageStudents
                );


                /* -----------------------------------------
                   NON-PAGINATED RESPONSE
                ----------------------------------------- */

                if (
                    Array.isArray(data)
                ) {
                    break;
                }


                /* -----------------------------------------
                   EMPTY PAGE
                ----------------------------------------- */

                if (
                    pageStudents.length ===
                    0
                ) {
                    break;
                }


                /* -----------------------------------------
                   USE TOTAL COUNT WHEN AVAILABLE
                ----------------------------------------- */

                if (
                    totalCount !== null
                ) {
                    const totalPages =
                        Math.ceil(
                            totalCount /
                                pageSize
                        );

                    if (
                        page >=
                        totalPages
                    ) {
                        break;
                    }
                }


                /* -----------------------------------------
                   FALLBACK WHEN COUNT IS NOT AVAILABLE
                ----------------------------------------- */

                else if (
                    pageStudents.length <
                    pageSize
                ) {
                    break;
                }


                page += 1;
            }


            /* =================================================
               REMOVE DUPLICATES
            ================================================= */

            const uniqueStudents =
                Array.from(
                    new Map(
                        allStudents
                            .filter(
                                student =>
                                    student?.id !==
                                        undefined &&
                                    student?.id !==
                                        null
                            )
                            .map(
                                student => [
                                    String(
                                        student.id
                                    ),
                                    student,
                                ]
                            )
                    ).values()
                );


            /* =================================================
               SORT ALPHABETICALLY
            ================================================= */

            uniqueStudents.sort(
                (a, b) =>
                    String(
                        a?.full_name ||
                        a?.name ||
                        ""
                    ).localeCompare(
                        String(
                            b?.full_name ||
                            b?.name ||
                            ""
                        ),
                        undefined,
                        {
                            sensitivity:
                                "base",
                        }
                    )
            );


            setStudents(
                uniqueStudents
            );


            /* =================================================
               DEBUG INFORMATION
            ================================================= */

            console.log(
                "Complaint student loader:",
                {
                    academicYear:
                        selectedYear.id,

                    apiTotal:
                        totalCount,

                    pagesLoaded:
                        page,

                    studentsLoaded:
                        uniqueStudents.length,

                    caseStudyStudents:
                        uniqueStudents.filter(
                            student =>
                                String(
                                    student?.student_type ||
                                        ""
                                )
                                    .trim()
                                    .toLowerCase() ===
                                "case study"
                        ).length,
                }
            );

        } catch (error) {
            console.error(
                "FETCH ALL COMPLAINT STUDENTS ERROR:",
                error
            );

            setStudents([]);

            message.error(
                "Unable to load all students"
            );
        } finally {
            setLoadingStudents(false);
        }
    };


    /* =====================================================
       FETCH COMPLAINTS
    ===================================================== */

    const fetchComplaints = async () => {
        if (!selectedYear?.id) {
            setComplaints([]);
            return;
        }

        try {
            setLoading(true);

            const res = await api.get(
                "/complaints/",
                {
                    params: {
                        academic_year:
                            selectedYear.id,
                    },
                }
            );


            const data =
                Array.isArray(res.data)
                    ? res.data
                    : res.data?.results || [];


            setComplaints(data);

        } catch (error) {
            console.error(
                "FETCH COMPLAINTS ERROR:",
                error
            );

            message.error(
                "Unable to load complaints"
            );
        } finally {
            setLoading(false);
        }
    };


    /* =====================================================
       LOAD DATA WHEN ACADEMIC YEAR IS READY
    ===================================================== */

    useEffect(() => {
        if (
            !userType ||
            !initialized ||
            !selectedYear?.id
        ) {
            return;
        }


        fetchComplaints();


        if (canPost) {
            fetchStudents();
            fetchPeriods();
        }


        if (
            normalizeRole(userType) !==
            "teacher"
        ) {
            fetchGradeClasses();
            fetchPeriods();
        }

    }, [
        userType,
        canPost,
        selectedYear?.id,
        initialized,
    ]);


    /* =====================================================
       FILTER COMPLAINTS
    ===================================================== */

    const filteredComplaints =
        complaints.filter(item => {
            const q =
                search
                    .trim()
                    .toLowerCase();


            const title =
                String(
                    item.title || ""
                ).toLowerCase();


            const studentName =
                String(
                    item.student_full_name ||
                        ""
                ).toLowerCase();


            const matchesSearch =
                !q ||
                title.includes(q) ||
                studentName.includes(q);


            const matchesPeriod =
                !selectedPeriod ||
                item.period ===
                    selectedPeriod;


            const matchesGradeClass =
                !selectedGradeClass ||
                item.grade_class ===
                    selectedGradeClass;


            return (
                matchesSearch &&
                matchesPeriod &&
                matchesGradeClass
            );
        });


    /* =====================================================
       OPEN CREATE
    ===================================================== */

    const openCreate = () => {
        setEditId(null);

        setSelectedStudent(null);

        form.resetFields();

        setModalOpen(true);

        /*
         * Refresh students when opening the form.
         * This makes sure the latest student list is available.
         */
        if (selectedYear?.id) {
            fetchStudents();
        }
    };


    /* =====================================================
       OPEN EDIT
    ===================================================== */

    const openEdit = item => {
        setEditId(item.id);


        setSelectedStudent({
            id: item.student,
            full_name:
                item.student_full_name,
        });


        form.setFieldsValue({
            title: item.title,
            description:
                item.description,
            period:
                item.period,
        });


        setModalOpen(true);
    };


    /* =====================================================
       SUBMIT COMPLAINT
    ===================================================== */

    const submitComplaint = async values => {
        if (!selectedStudent) {
            message.warning(
                "Select a student first"
            );

            return;
        }


        if (!values.period) {
            message.warning(
                "Please select complaint period"
            );

            return;
        }


        const payload = {
            student:
                selectedStudent.id,

            period:
                values.period,

            title:
                values.title,

            description:
                values.description,
        };


        try {
            if (editId) {
                const res =
                    await api.patch(
                        `/complaints/${editId}/`,
                        payload
                    );


                setComplaints(prev =>
                    prev.map(c =>
                        c.id === editId
                            ? res.data
                            : c
                    )
                );


                message.success(
                    "Complaint updated"
                );

            } else {
                const res =
                    await api.post(
                        "/complaints/",
                        payload
                    );


                setComplaints(prev => [
                    res.data,
                    ...prev,
                ]);


                message.success(
                    "Complaint submitted"
                );
            }


            setModalOpen(false);

            form.resetFields();

            setEditId(null);

            setSelectedStudent(null);

        } catch (error) {
            console.error(
                "SUBMIT COMPLAINT ERROR:",
                error
            );

            message.error(
                error?.response?.data?.detail ||
                    "Failed to submit complaint"
            );
        }
    };


    /* =====================================================
       DELETE COMPLAINT
    ===================================================== */

    const deleteComplaint = async id => {
        try {
            await api.delete(
                `/complaints/${id}/`
            );


            setComplaints(prev =>
                prev.filter(
                    c => c.id !== id
                )
            );


            message.success(
                "Complaint removed"
            );

        } catch (error) {
            console.error(
                "DELETE COMPLAINT ERROR:",
                error
            );

            message.error(
                "Delete failed"
            );
        }
    };


    /* =====================================================
       CLOSE MODAL
    ===================================================== */

    const closeModal = () => {
        setModalOpen(false);

        setEditId(null);

        setSelectedStudent(null);

        form.resetFields();
    };


    /* =====================================================
       PERMISSION SCREEN
    ===================================================== */

    if (!canPost) {
        return (
            <Card>
                <Typography.Text
                    type="danger"
                >
                    You do not have permission
                    to access complaints.
                </Typography.Text>
            </Card>
        );
    }


    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <Layout
            style={{
                background: "#e5e7eb",
                minHeight: "100%",
                padding: 20,
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <Card
                style={{
                    maxWidth: 1400,
                    width: "100%",
                    margin: "auto",
                    background: "#111827",
                    borderRadius: 20,
                }}
            >
                <Space
                    direction="vertical"
                    style={{
                        width: "100%",
                    }}
                >

                    <Title
                        level={2}
                        style={{
                            color: "#ffffff",
                            textAlign: "center",
                            marginBottom: 0,
                        }}
                    >
                        <SafetyCertificateOutlined />

                        {" "}

                        School Complaint
                        Investigation Unit
                    </Title>


                    <Text
                        style={{
                            color: "#d1d5db",
                            display: "block",
                            textAlign: "center",
                        }}
                    >
                        Student complaint monitoring
                        and administrative review
                        system
                    </Text>


                    {selectedYear && (
                        <div
                            style={{
                                textAlign:
                                    "center",
                                marginTop: 12,
                            }}
                        >
                            <Tag
                                color="volcano"
                                style={{
                                    fontSize: 15,
                                    fontWeight:
                                        "bold",
                                    padding:
                                        "6px 14px",
                                }}
                            >
                                Academic Year:
                                {" "}
                                {
                                    selectedYear.name
                                }
                            </Tag>
                        </div>
                    )}

                </Space>
            </Card>


            {/* =================================================
                SEARCH / ACTIONS
            ================================================= */}

            <Card
                style={{
                    marginTop: 20,
                    borderRadius: 18,
                }}
            >
                <Row
                    justify="space-between"
                    gutter={[
                        16,
                        16,
                    ]}
                    align="middle"
                >

                    <Col
                        xs={24}
                        md={16}
                    >
                        <Input
                            size="large"
                            placeholder="Search complaint records..."
                            prefix={
                                <SearchOutlined />
                            }
                            value={search}
                            onChange={e =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />
                    </Col>


                    <Col
                        xs={24}
                        md={8}
                    >
                        <Button
                            block
                            size="large"
                            type="primary"
                            icon={
                                <PlusOutlined />
                            }
                            onClick={
                                openCreate
                            }
                            style={{
                                background:
                                    "#6b0f1a",
                                borderColor:
                                    "#6b0f1a",
                            }}
                        >
                            New Complaint
                        </Button>
                    </Col>

                </Row>
            </Card>


            {/* =================================================
                FILTERS
            ================================================= */}

            {normalizeRole(
                userType
            ) !== "teacher" && (
                <Card
                    style={{
                        marginTop: 20,
                        borderRadius: 18,
                    }}
                >
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                    >

                        <Col
                            xs={24}
                            md={8}
                        >
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Filter Grade Class"
                                style={{
                                    width: "100%",
                                }}
                                value={
                                    selectedGradeClass
                                }
                                onChange={
                                    setSelectedGradeClass
                                }
                                options={gradeClasses.map(
                                    item => ({
                                        value:
                                            item.id,
                                        label:
                                            item.name,
                                    })
                                )}
                            />
                        </Col>


                        <Col
                            xs={24}
                            md={8}
                        >
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Filter Period"
                                style={{
                                    width: "100%",
                                }}
                                value={
                                    selectedPeriod
                                }
                                onChange={
                                    setSelectedPeriod
                                }
                                options={periods.map(
                                    item => ({
                                        value:
                                            item.id,
                                        label:
                                            item.name,
                                    })
                                )}
                            />
                        </Col>


                        <Col
                            xs={24}
                            md={8}
                        >
                            <Button
                                block
                                icon={
                                    <FilterOutlined />
                                }
                                onClick={() => {
                                    setSelectedPeriod(
                                        null
                                    );

                                    setSelectedGradeClass(
                                        null
                                    );
                                }}
                            >
                                Clear Filters
                            </Button>
                        </Col>

                    </Row>
                </Card>
            )}


            <Divider />


            {/* =================================================
                COMPLAINT CARDS
            ================================================= */}

            {loading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent:
                            "center",
                        padding: 40,
                    }}
                >
                    <Spin size="large" />
                </div>
            ) : filteredComplaints.length ===
              0 ? (
                <Card
                    style={{
                        borderRadius: 18,
                    }}
                >
                    <Empty
                        description={
                            search ||
                            selectedPeriod ||
                            selectedGradeClass
                                ? "No complaints match your filters"
                                : "No complaint records found"
                        }
                    />
                </Card>
            ) : (
                <Row
                    gutter={[
                        20,
                        20,
                    ]}
                >

                    {filteredComplaints.map(
                        item => (
                            <Col
                                xs={24}
                                sm={12}
                                lg={8}
                                key={item.id}
                            >

                                <Card
                                    hoverable
                                    style={{
                                        borderRadius:
                                            18,
                                        borderTop:
                                            "5px solid #6b0f1a",
                                        height:
                                            "100%",
                                    }}
                                >

                                    <Space>
                                        <Avatar
                                            size={45}
                                            icon={
                                                <UserOutlined />
                                            }
                                            style={{
                                                background:
                                                    "#111827",
                                            }}
                                        />

                                        <div>
                                            <Text
                                                strong
                                            >
                                                {
                                                    item.student_full_name
                                                }
                                            </Text>

                                            <br />

                                            <Badge
                                                status="processing"
                                                text="Under Review"
                                            />

                                            <br />

                                            <Tag
                                                color="blue"
                                                style={{
                                                    marginTop:
                                                        8,
                                                    fontWeight:
                                                        "600",
                                                }}
                                            >
                                                Period:{" "}
                                                {
                                                    item.period_name ||
                                                    "Not Assigned"
                                                }
                                            </Tag>
                                        </div>
                                    </Space>


                                    <Divider />


                                    <Title
                                        level={5}
                                    >
                                        <FileProtectOutlined />

                                        {" "}

                                        {item.title}
                                    </Title>


                                    <Text>
                                        {
                                            item.description
                                        }
                                    </Text>


                                    <Divider />


                                    <Tag color="red">
                                        Official Complaint
                                    </Tag>


                                    <div
                                        style={{
                                            marginTop:
                                                15,
                                        }}
                                    >
                                        <Space>

                                            <Button
                                                icon={
                                                    <EditOutlined />
                                                }
                                                onClick={() =>
                                                    openEdit(
                                                        item
                                                    )
                                                }
                                            >
                                                Edit
                                            </Button>


                                            <Popconfirm
                                                title="Delete complaint?"
                                                description="This complaint will be permanently removed."
                                                okText="Delete"
                                                cancelText="Cancel"
                                                onConfirm={() =>
                                                    deleteComplaint(
                                                        item.id
                                                    )
                                                }
                                            >
                                                <Button
                                                    danger
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                >
                                                    Delete
                                                </Button>
                                            </Popconfirm>

                                        </Space>
                                    </div>

                                </Card>

                            </Col>
                        )
                    )}

                </Row>
            )}


            {/* =================================================
                CREATE / EDIT MODAL
            ================================================= */}

            <Modal
                open={modalOpen}
                title={
                    editId
                        ? "Update Complaint"
                        : "Create Complaint"
                }
                onCancel={closeModal}
                footer={null}
                destroyOnClose={false}
                width={650}
            >

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={
                        submitComplaint
                    }
                >

                    {/* =========================================
                        STUDENT
                    ========================================= */}

                    <Form.Item
                        label="Student"
                        required
                    >

                        <Select
                            showSearch
                            allowClear
                            loading={
                                loadingStudents
                            }
                            size="large"
                            placeholder="Search and select student"
                            value={
                                selectedStudent?.id ??
                                undefined
                            }
                            optionFilterProp="label"
                            filterOption={(
                                input,
                                option
                            ) => {
                                const searchValue =
                                    String(
                                        input ||
                                            ""
                                    )
                                        .trim()
                                        .toLowerCase();


                                const label =
                                    String(
                                        option?.label ||
                                            ""
                                    ).toLowerCase();


                                return label.includes(
                                    searchValue
                                );
                            }}
                            options={students.map(
                                student => {
                                    const studentName =
                                        student?.full_name ||
                                        student?.name ||
                                        `Student #${student?.id}`;


                                    const studentType =
                                        String(
                                            student?.student_type ||
                                                ""
                                        ).trim();


                                    const gradeClass =
                                        student?.grade_class_name ||
                                        "";


                                    let label =
                                        studentName;


                                    if (
                                        studentType
                                    ) {
                                        label +=
                                            ` — ${studentType}`;
                                    }


                                    if (
                                        gradeClass
                                    ) {
                                        label +=
                                            ` — ${gradeClass}`;
                                    }


                                    return {
                                        value:
                                            student.id,

                                        label,

                                        student,
                                    };
                                }
                            )}
                            onChange={(
                                id,
                                option
                            ) => {
                                if (
                                    id ===
                                        undefined ||
                                    id ===
                                        null ||
                                    id === ""
                                ) {
                                    setSelectedStudent(
                                        null
                                    );

                                    return;
                                }


                                const student =
                                    option?.student ||
                                    students.find(
                                        s =>
                                            String(
                                                s.id
                                            ) ===
                                            String(
                                                id
                                            )
                                    );


                                setSelectedStudent(
                                    student ||
                                        null
                                );
                            }}
                            notFoundContent={
                                loadingStudents ? (
                                    <div
                                        style={{
                                            padding:
                                                20,
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <Spin
                                            size="small"
                                        />

                                        <div
                                            style={{
                                                marginTop:
                                                    8,
                                                color:
                                                    "#6b7280",
                                            }}
                                        >
                                            Loading all students...
                                        </div>
                                    </div>
                                ) : (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description="No student found"
                                    />
                                )
                            }
                        />

                    </Form.Item>


                    {/* =========================================
                        STUDENT INFORMATION
                    ========================================= */}

                    {selectedStudent && (
                        <Card
                            size="small"
                            style={{
                                marginBottom: 20,
                                background:
                                    "#f8fafc",
                                borderRadius:
                                    12,
                            }}
                        >
                            <Space
                                align="start"
                            >

                                <Avatar
                                    size={48}
                                    icon={
                                        <UserOutlined />
                                    }
                                    src={
                                        selectedStudent?.profile_image ||
                                        selectedStudent?.image ||
                                        undefined
                                    }
                                />


                                <div>

                                    <Text strong>
                                        {
                                            selectedStudent?.full_name ||
                                            selectedStudent?.name ||
                                            "Selected Student"
                                        }
                                    </Text>


                                    <br />


                                    {selectedStudent?.grade_class_name && (
                                        <Text type="secondary">
                                            Grade Class:{" "}
                                            {
                                                selectedStudent.grade_class_name
                                            }
                                        </Text>
                                    )}


                                    {selectedStudent?.student_type && (
                                        <>
                                            <br />

                                            <Tag
                                                color={
                                                    String(
                                                        selectedStudent.student_type
                                                    )
                                                        .toLowerCase() ===
                                                    "case study"
                                                        ? "gold"
                                                        : "blue"
                                                }
                                                style={{
                                                    marginTop:
                                                        6,
                                                }}
                                            >
                                                {
                                                    selectedStudent.student_type
                                                }
                                            </Tag>
                                        </>
                                    )}

                                </div>

                            </Space>
                        </Card>
                    )}


                    {/* =========================================
                        PERIOD
                    ========================================= */}

                    <Form.Item
                        name="period"
                        label="Complaint Period"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Select complaint period",
                            },
                        ]}
                    >

                        <Select
                            showSearch
                            optionFilterProp="label"
                            size="large"
                            placeholder="Select complaint period"
                            options={periods.map(
                                period => ({
                                    value:
                                        period.id,
                                    label:
                                        period.name,
                                })
                            )}
                        />

                    </Form.Item>


                    {/* =========================================
                        TITLE
                    ========================================= */}

                    <Form.Item
                        name="title"
                        label="Complaint Title"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Please enter the complaint title",
                            },
                        ]}
                    >

                        <Input
                            size="large"
                            placeholder="Enter complaint title"
                        />

                    </Form.Item>


                    {/* =========================================
                        DESCRIPTION
                    ========================================= */}

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Please enter the complaint description",
                            },
                        ]}
                    >

                        <TextArea
                            rows={5}
                            placeholder="Describe the complaint in detail..."
                            showCount
                            maxLength={2000}
                        />

                    </Form.Item>


                    {/* =========================================
                        ACTIONS
                    ========================================= */}

                    <Space
                        style={{
                            width: "100%",
                            justifyContent:
                                "flex-end",
                        }}
                    >

                        <Button
                            onClick={
                                closeModal
                            }
                        >
                            Cancel
                        </Button>


                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{
                                background:
                                    "#6b0f1a",
                                borderColor:
                                    "#6b0f1a",
                            }}
                        >
                            {editId
                                ? "Update Complaint"
                                : "Submit Complaint"}
                        </Button>

                    </Space>

                </Form>

            </Modal>

        </Layout>
    );
};


export default Complaint;